import { z } from "zod/v4";

import { z_ChatAttachmentRef } from "./chat";
import { z_ManualHoldingInput } from "./portfolio/manual-holding.types";

/**
 * Canonical Prism chat protocol contracts shared by backend, web, and mobile.
 *
 * Backend owns persistence and orchestration behavior, but the wire/runtime
 * schemas live here so clients can validate long-lived stream payloads before
 * applying them to UI state.
 */

const _z_RawUserChatMessage = z.object({
  cuid: z.string(),
  role: z.literal("user"),
  message: z.string(),
  tagName: z.string().optional(),
});
export type T_RawUserChatMessage = z.infer<typeof _z_RawUserChatMessage>;

export const z_ToolCall = z.object({
  name: z.string(),
  description: z.string(),
  args: z.record(z.string(), z.string().nullish()),
  response: z.string(),
  error: z.boolean().nullish(),
  status: z.enum(["pending", "completed", "error"]).nullish(),
  iterationIndex: z.number().nullish(),
  iterationType: z.enum(["research", "mutation"]).nullish(),
});
export type T_ToolCall = z.infer<typeof z_ToolCall>;

export const z_ChatMutationType = z.enum([
  "setAlert",
  "removeAlert",
  "addToWatchlist",
  "removeFromWatchlist",
  "setManualHolding",
  "removeManualHolding",
  "setEventAlert",
  "setCalendarAlert",
  "removeEventAlert",
  "connectBrokerage",
]);
export type T_ChatMutationType = z.infer<typeof z_ChatMutationType>;

export const z_ChatMutation = z.object({
  cuid: z.string(),
  mutationType: z_ChatMutationType,
  mutationData: z.record(z.string(), z.unknown()),
  description: z.string(),
  status: z.enum(["PENDING", "ACCEPTED", "REJECTED"]),
  error: z.string().nullish(),
});
export type T_ChatMutation = z.infer<typeof z_ChatMutation>;

export const z_SetAlertMutationData = z.object({
  symbol: z.string(),
  value: z.number(),
  unit: z.enum(["PRICE", "PERCENT", "INTRADAY"]),
  notes: z.string().nullable().optional(),
});

export const z_RemoveAlertMutationData = z.object({
  alertId: z.string().nullable().optional(),
  symbol: z.string().nullable().optional(),
});

export const z_WatchlistMutationData = z.object({
  symbol: z.string(),
});

export const z_ManualHoldingMutationData = z_ManualHoldingInput;

export const z_RemoveManualHoldingMutationData = z.object({
  symbol: z.string(),
});

export const z_SetEventAlertMutationData = z.object({
  trigger_condition: z.string(),
  original_user_request: z.string(),
});

export const z_SetCalendarAlertMutationData = z.object({
  title: z.string(),
  event_date: z.string(),
  description: z.string(),
  source_url: z.string(),
  ticker: z.string().nullish(),
  trigger_condition: z.string(),
  original_user_request: z.string(),
});

export const z_RemoveEventAlertMutationData = z.object({
  eventAlertId: z.string(),
});

export const z_DbChatMutation = z.object({
  cuid: z.string(),
  mutation_type: z_ChatMutationType,
  mutation_data: z.record(z.string(), z.unknown()),
  description: z.string(),
  status: z.enum(["PENDING", "ACCEPTED", "REJECTED"]),
  error: z.string().nullable(),
});

export const z_JsonRecord = z.record(z.string(), z.unknown());

export type T_JsonObject = { [key: string]: T_JsonValue };
export type T_JsonValue =
  | string
  | number
  | boolean
  | null
  | T_JsonObject
  | T_JsonValue[];

export const z_JsonValue: z.ZodType<T_JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z_JsonValue),
    z.record(z.string(), z_JsonValue),
  ]),
);

export const z_InputJsonValue: z.ZodType<T_JsonObject> = z.record(
  z.string(),
  z_JsonValue,
);

const z_ResearchIteration = z.object({
  type: z.literal("research"),
  toolCalls: z.array(z_ToolCall),
});

const z_MutationIteration = z.object({
  type: z.literal("mutation"),
  mutations: z.array(z.record(z.string(), z.unknown())),
});

const z_CorrectionIteration = z.object({
  type: z.literal("correction"),
  errorMessage: z.string(),
  rawLlmResponse: z.string().optional(),
});

export const z_OrchestratorIteration = z.discriminatedUnion("type", [
  z_ResearchIteration,
  z_MutationIteration,
  z_CorrectionIteration,
]);
export type T_OrchestratorIteration = z.infer<typeof z_OrchestratorIteration>;

export const z_AssistantMessage = z.object({
  cuid: z.string(),
  role: z.literal("assistant"),
  message: z.string(),
  toolCalls: z.array(z_ToolCall).nullish(),
  followUpQuestions: z.array(z.string()).nullish(),
  mutations: z.array(z_ChatMutation).nullish(),
  iterations: z.array(z_OrchestratorIteration).nullish(),
  liked: z.boolean().nullish(),
  reason: z.string().nullish(),
});
export type T_AssistantMessage = z.infer<typeof z_AssistantMessage>;

export const z_UserMessage = z.object({
  cuid: z.string(),
  role: z.literal("user"),
  message: z.string(),
  timestamp: z.string(),
  attachments: z.array(z_ChatAttachmentRef).optional(),
});

export const z_Message = z.discriminatedUnion("role", [
  z_AssistantMessage,
  z_UserMessage,
]);
export type T_Message = z.infer<typeof z_Message>;

type T_SetAlertMutation = {
  tool: "setAlert";
  symbol: string;
  value: number;
  unit: "PRICE" | "PERCENT" | "INTRADAY";
  notes?: string | null;
};

type T_RemoveAlertMutation = {
  tool: "removeAlert";
  alertId?: string | null;
  symbol?: string | null;
};

type T_AddToWatchlistMutation = {
  tool: "addToWatchlist";
  symbol: string;
};

type T_RemoveFromWatchlistMutation = {
  tool: "removeFromWatchlist";
  symbol: string;
};

type T_SetManualHoldingMutation = {
  tool: "setManualHolding";
  symbol: string;
  units: number;
  is_crypto?: boolean;
};

type T_RemoveManualHoldingMutation = {
  tool: "removeManualHolding";
  symbol: string;
};

type T_SetEventAlertMutation = {
  tool: "setEventAlert";
  trigger_condition: string;
  original_user_request: string;
};

type T_SetCalendarAlertMutation = {
  tool: "setCalendarAlert";
  title: string;
  event_date: string;
  description: string;
  source_url: string;
  ticker?: string | null;
  trigger_condition: string;
  original_user_request: string;
};

type T_RemoveEventAlertMutation = {
  tool: "removeEventAlert";
  eventAlertId: string;
};

export type T_PendingMutation =
  | T_SetAlertMutation
  | T_RemoveAlertMutation
  | T_AddToWatchlistMutation
  | T_RemoveFromWatchlistMutation
  | T_SetManualHoldingMutation
  | T_RemoveManualHoldingMutation
  | T_SetEventAlertMutation
  | T_SetCalendarAlertMutation
  | T_RemoveEventAlertMutation;

export type T_StreamResponse = T_AssistantMessage;

export const z_ThreadCompactionMeta = z.object({
  estimatedTokenCount: z.number(),
  warningTokenThreshold: z.number(),
  hardTokenThreshold: z.number(),
  estimatedTokensUntilCompaction: z.number(),
  shouldWarn: z.boolean(),
  shouldCompact: z.boolean(),
  parentThreadCuid: z.string().nullable().optional(),
  parentThreadName: z.string().nullable().optional(),
  rootThreadCuid: z.string().nullable().optional(),
  hasCompactionContext: z.boolean().optional(),
});
export type T_ThreadCompactionMeta = z.infer<typeof z_ThreadCompactionMeta>;

export type T_Thread = {
  cuid: string;
  name: string | null;
  created_at: Date;
  updated_at: Date;
  is_shared: boolean | null;
  compaction?: T_ThreadCompactionMeta;
};

export type T_ThreadSearchResult = T_Thread & {
  matchSnippet: string | null;
};

export const E_ChatAgentRunStatus = {
  CREATED: "CREATED",
  RUNNING: "RUNNING",
  WAITING_FOR_APPROVAL: "WAITING_FOR_APPROVAL",
  RESUMING: "RESUMING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
} as const;

export const E_ChatAgentActionStatus = {
  PROPOSED: "PROPOSED",
  AWAITING_APPROVAL: "AWAITING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  EXECUTING: "EXECUTING",
  EXECUTED: "EXECUTED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
} as const;

export const E_ChatAgentActionRejectBehavior = {
  RESUME: "RESUME",
  CANCEL_RUN: "CANCEL_RUN",
} as const;

export const CHAT_AGENT_RUN_STATUS_VALUES = [
  E_ChatAgentRunStatus.CREATED,
  E_ChatAgentRunStatus.RUNNING,
  E_ChatAgentRunStatus.WAITING_FOR_APPROVAL,
  E_ChatAgentRunStatus.RESUMING,
  E_ChatAgentRunStatus.COMPLETED,
  E_ChatAgentRunStatus.FAILED,
  E_ChatAgentRunStatus.CANCELLED,
  E_ChatAgentRunStatus.EXPIRED,
] as const;

export const CHAT_AGENT_ACTION_STATUS_VALUES = [
  E_ChatAgentActionStatus.PROPOSED,
  E_ChatAgentActionStatus.AWAITING_APPROVAL,
  E_ChatAgentActionStatus.APPROVED,
  E_ChatAgentActionStatus.REJECTED,
  E_ChatAgentActionStatus.EXECUTING,
  E_ChatAgentActionStatus.EXECUTED,
  E_ChatAgentActionStatus.FAILED,
  E_ChatAgentActionStatus.CANCELLED,
  E_ChatAgentActionStatus.EXPIRED,
] as const;

export const CHAT_AGENT_ACTION_REJECT_BEHAVIOR_VALUES = [
  E_ChatAgentActionRejectBehavior.RESUME,
  E_ChatAgentActionRejectBehavior.CANCEL_RUN,
] as const;

export const z_ChatAgentLegacyMutationState = z.object({
  cuid: z.string(),
  mutationType: z.string(),
  status: z.string(),
});
export type T_ChatAgentLegacyMutationState = z.infer<
  typeof z_ChatAgentLegacyMutationState
>;

export const z_ChatAgentActionState = z.object({
  cuid: z.string(),
  runCuid: z.string(),
  toolName: z.string(),
  title: z.string(),
  description: z.string(),
  input: z_JsonValue,
  status: z.enum(CHAT_AGENT_ACTION_STATUS_VALUES),
  version: z.number(),
  approvalRequired: z.boolean(),
  idempotencyKey: z.string(),
  attemptCount: z.number(),
  result: z_JsonValue.nullable(),
  error: z.string().nullable(),
  approvalPolicyVersion: z.string().nullable(),
  onReject: z.enum(CHAT_AGENT_ACTION_REJECT_BEHAVIOR_VALUES),
  cancellationRequestedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  approvedAt: z.string().nullable(),
  executedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  legacyMutations: z.array(z_ChatAgentLegacyMutationState),
});
export type T_ChatAgentActionState = z.infer<typeof z_ChatAgentActionState>;

export const z_ChatAgentRunState = z.object({
  cuid: z.string(),
  threadCuid: z.string(),
  initiatingMessageCuid: z.string().nullable(),
  assistantMessageCuid: z.string().nullable(),
  status: z.enum(CHAT_AGENT_RUN_STATUS_VALUES),
  version: z.number(),
  workerId: z.string().nullable(),
  workerLeaseUntil: z.string().nullable(),
  resumeState: z_JsonValue.nullable(),
  error: z.string().nullable(),
  metadata: z_JsonValue.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  actions: z.array(z_ChatAgentActionState),
});
export type T_ChatAgentRunState = z.infer<typeof z_ChatAgentRunState>;

export const z_ThreadEvent = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("user_message"),
    data: z.object({
      cuid: z.string(),
      message: z.string(),
      attachments: z.array(z_ChatAttachmentRef).optional(),
    }),
  }),
  z.object({
    type: z.literal("messages_deleted"),
    messageCuids: z.array(z.string()),
  }),
  z.object({
    type: z.literal("message_liked"),
    messageCuid: z.string(),
    liked: z.boolean().nullable(),
    reason: z.string().nullable().optional(),
  }),
  z.object({ type: z.literal("mutation_accepted"), mutationCuid: z.string() }),
  z.object({ type: z.literal("mutation_rejected"), mutationCuid: z.string() }),
  z.object({ type: z.literal("generation_update"), data: z_AssistantMessage }),
  z.object({
    type: z.literal("generation_complete"),
    data: z_AssistantMessage,
  }),
  z.object({
    type: z.literal("generation_cancelled"),
    data: z_AssistantMessage,
  }),
  z.object({ type: z.literal("generation_error"), error: z.string() }),
  z.object({
    type: z.literal("thread_history"),
    threadCuid: z.string(),
    messages: z.array(z_Message),
    threadName: z.string().nullable(),
    isClaimed: z.boolean(),
    isOwned: z.boolean(),
    hasActiveGeneration: z.boolean(),
    compaction: z_ThreadCompactionMeta.optional(),
  }),
  z.object({
    type: z.literal("thread_compacted"),
    data: z.object({
      oldThreadCuid: z.string(),
      newThreadCuid: z.string(),
      summaryMessageCount: z.number(),
    }),
  }),
]);
export type T_ThreadEvent = z.infer<typeof z_ThreadEvent>;

export const z_ThreadAgentStateUpdatedEvent = z.object({
  type: z.literal("agent_state_updated"),
  threadCuid: z.string(),
  runCuids: z.array(z.string()).optional(),
  actionCuids: z.array(z.string()).optional(),
  mutationCuids: z.array(z.string()).optional(),
  reason: z.string(),
});
export type T_ThreadAgentStateUpdatedEvent = z.infer<
  typeof z_ThreadAgentStateUpdatedEvent
>;
export type T_ChatV7AgentStateUpdatedEvent = T_ThreadAgentStateUpdatedEvent;

export const z_ThreadStreamEvent = z.union([
  z_ThreadEvent,
  z_ThreadAgentStateUpdatedEvent,
]);
export type T_ThreadStreamEvent = z.infer<typeof z_ThreadStreamEvent>;

export const z_ChatV7ThreadHistoryEvent = z.object({
  type: z.literal("thread_history"),
  threadCuid: z.string(),
  messages: z.array(z_Message),
  threadName: z.string().nullable(),
  isClaimed: z.boolean(),
  isOwned: z.boolean(),
  hasActiveGeneration: z.boolean(),
  compaction: z_ThreadCompactionMeta.optional(),
  agentRuns: z.array(z_ChatAgentRunState),
});
export type T_ChatV7ThreadHistoryEvent = z.infer<
  typeof z_ChatV7ThreadHistoryEvent
>;

export const z_ChatV7ThreadEvent = z.union([
  z.object({
    type: z.literal("user_message"),
    data: z.object({
      cuid: z.string(),
      message: z.string(),
      attachments: z.array(z_ChatAttachmentRef).optional(),
    }),
  }),
  z.object({
    type: z.literal("messages_deleted"),
    messageCuids: z.array(z.string()),
  }),
  z.object({
    type: z.literal("message_liked"),
    messageCuid: z.string(),
    liked: z.boolean().nullable(),
    reason: z.string().nullable().optional(),
  }),
  z.object({ type: z.literal("mutation_accepted"), mutationCuid: z.string() }),
  z.object({ type: z.literal("mutation_rejected"), mutationCuid: z.string() }),
  z.object({ type: z.literal("generation_update"), data: z_AssistantMessage }),
  z.object({
    type: z.literal("generation_complete"),
    data: z_AssistantMessage,
  }),
  z.object({
    type: z.literal("generation_cancelled"),
    data: z_AssistantMessage,
  }),
  z.object({ type: z.literal("generation_error"), error: z.string() }),
  z.object({
    type: z.literal("thread_compacted"),
    data: z.object({
      oldThreadCuid: z.string(),
      newThreadCuid: z.string(),
      summaryMessageCount: z.number(),
    }),
  }),
  z_ChatV7ThreadHistoryEvent,
  z_ThreadAgentStateUpdatedEvent,
]);
export type T_ChatV7ThreadEvent = z.infer<typeof z_ChatV7ThreadEvent>;
